/**
 * Keyboard behaviour of the request-access dialog: initial focus, the Tab
 * trap, and Escape. Focus *return* to the opener is the page's, because it
 * has to happen after the stage stops being inert.
 */

import { useEffect, type RefObject } from 'react';

const FOCUSABLE = 'button, input, a[href], [tabindex]:not([tabindex="-1"])';

/** The card's tabbable elements, in document order. */
export function tabbableWithin(card: HTMLElement): HTMLElement[] {
  return [...card.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (element) =>
      !(element as HTMLButtonElement).disabled &&
      element.getAttribute('aria-hidden') !== 'true' &&
      !element.hidden,
  );
}

export function useDialogFocus({
  card,
  initialFocus,
  onEscape,
}: {
  readonly card: RefObject<HTMLElement | null>;
  /** Which element receives focus when the dialog opens. */
  readonly initialFocus: () => HTMLElement | null;
  readonly onEscape: () => void;
}): void {
  // Initial focus, once the card has painted: the reference waits a frame so
  // the entrance animation starts from a settled layout.
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      initialFocus()?.focus();
    });
    return () => {
      cancelAnimationFrame(frame);
    };
    // Only on mount: the dialog is mounted while open and unmounted on close.
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onEscape();
        return;
      }
      if (event.key !== 'Tab' || card.current === null) return;
      const tabbable = tabbableWithin(card.current);
      if (tabbable.length === 0) return;
      const first = tabbable[0];
      const last = tabbable[tabbable.length - 1];
      if (first === undefined || last === undefined) return;
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && card.current.contains(active);
      if (event.shiftKey) {
        if (!inside || active === first) {
          event.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [card, onEscape]);
}
